import { openDB, type IDBPDatabase } from 'idb';
import type { GeneratedItem, ItemId, RecipeRecord } from '@/types/domain';
import { buildSeeds } from '@/data/seedRecipes';
import { isSaveFile, normalizeImportedSave, type SaveBundle } from '@/schemas/saveSchema';

const DB_NAME = 'infinite-crafting-table';
const DB_VERSION = 1;

export interface HistoryEntry {
  id: string;
  at: number;
  key: string;
  resultId: ItemId | null;
  isVanilla: boolean;
  label: string;
}

let dbPromise: Promise<IDBPDatabase> | null = null;

function getDB(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        // Versioned migrations: add stores as the version grows.
        if (oldVersion < 1) {
          db.createObjectStore('items', { keyPath: 'id' });
          db.createObjectStore('recipes', { keyPath: 'key' });
          db.createObjectStore('settings');
          db.createObjectStore('favorites', { keyPath: 'id' });
          db.createObjectStore('history', { keyPath: 'id' });
          db.createObjectStore('meta');
        }
      },
    });
  }
  return dbPromise;
}

async function markInitialized(): Promise<void> {
  const db = await getDB();
  await db.put('meta', true, 'seeded');
}

export const SaveManager = {
  async init(): Promise<void> {
    await getDB();
    await markInitialized();
  },

  /** Load the curated seed recipes on demand (e.g. a "Load example recipes" button). */
  async loadSeeds(): Promise<{ items: number }> {
    const db = await getDB();
    const seeds = buildSeeds();
    const tx = db.transaction(['items', 'recipes', 'meta'], 'readwrite');
    for (const { item, record } of seeds) {
      await tx.objectStore('items').put(item);
      await tx.objectStore('recipes').put(record);
    }
    await tx.objectStore('meta').put(true, 'seeded');
    await tx.done;
    return { items: seeds.length };
  },

  // ---- items ----
  async putItem(item: GeneratedItem): Promise<void> {
    const db = await getDB();
    await db.put('items', item);
  },
  async getItem(id: ItemId): Promise<GeneratedItem | undefined> {
    const db = await getDB();
    return (await db.get('items', id)) as GeneratedItem | undefined;
  },
  async allItems(): Promise<GeneratedItem[]> {
    const db = await getDB();
    return (await db.getAll('items')) as GeneratedItem[];
  },
  async deleteItem(id: ItemId): Promise<void> {
    const db = await getDB();
    await db.delete('items', id);
  },

  // ---- recipes ----
  async putRecipe(record: RecipeRecord): Promise<void> {
    const db = await getDB();
    await db.put('recipes', record);
  },
  async getRecipe(key: string): Promise<RecipeRecord | undefined> {
    const db = await getDB();
    return (await db.get('recipes', key)) as RecipeRecord | undefined;
  },
  async deleteRecipe(key: string): Promise<void> {
    const db = await getDB();
    await db.delete('recipes', key);
  },
  async allRecipes(): Promise<RecipeRecord[]> {
    const db = await getDB();
    return (await db.getAll('recipes')) as RecipeRecord[];
  },

  // ---- history ----
  async addHistory(entry: HistoryEntry): Promise<void> {
    const db = await getDB();
    await db.put('history', entry);
  },
  async allHistory(): Promise<HistoryEntry[]> {
    const db = await getDB();
    const all = (await db.getAll('history')) as HistoryEntry[];
    return all.sort((a, b) => b.at - a.at);
  },
  async clearHistory(): Promise<void> {
    const db = await getDB();
    await db.clear('history');
  },

  // ---- favorites ----
  async addFavorite(id: ItemId): Promise<void> {
    const db = await getDB();
    await db.put('favorites', { id, at: Date.now() });
  },
  async removeFavorite(id: ItemId): Promise<void> {
    const db = await getDB();
    await db.delete('favorites', id);
  },
  async favorites(): Promise<ItemId[]> {
    const db = await getDB();
    const all = (await db.getAll('favorites')) as Array<{ id: ItemId; at: number }>;
    return all.sort((a, b) => b.at - a.at).map((f) => f.id);
  },

  // ---- settings ----
  async getSetting<T>(key: string, fallback: T): Promise<T> {
    const db = await getDB();
    const v = await db.get('settings', key);
    return (v === undefined ? fallback : (v as T));
  },
  async setSetting<T>(key: string, value: T): Promise<void> {
    const db = await getDB();
    await db.put('settings', value, key);
  },

  // ---- meta ----
  async getMeta<T>(key: string, fallback: T): Promise<T> {
    const db = await getDB();
    const v = await db.get('meta', key);
    return (v === undefined ? fallback : (v as T));
  },
  async setMeta<T>(key: string, value: T): Promise<void> {
    const db = await getDB();
    await db.put('meta', value, key);
  },

  // ---- export / import / reset ----
  async exportSave(): Promise<SaveBundle> {
    const db = await getDB();
    const [items, recipes, history, favorites] = await Promise.all([
      db.getAll('items'),
      db.getAll('recipes'),
      db.getAll('history'),
      db.getAll('favorites'),
    ]);
    const settings: Record<string, unknown> = {};
    const settingsCursor = await db.getAllKeys('settings');
    for (const k of settingsCursor) settings[k as string] = await db.get('settings', k);
    return {
      app: 'infinite-crafting-table',
      version: DB_VERSION,
      exportedAt: Date.now(),
      items: items as GeneratedItem[],
      recipes: recipes as RecipeRecord[],
      history: history as HistoryEntry[],
      favorites: favorites as Array<{ id: ItemId; at: number }>,
      settings,
    };
  },

  async importSave(data: unknown): Promise<{ items: number; recipes: number }> {
    const bundle = normalizeImportedSave(data);
    if (!bundle) throw new Error('Invalid save file: failed validation.');
    const db = await getDB();
    const tx = db.transaction(['items', 'recipes', 'history', 'favorites', 'settings', 'meta'], 'readwrite');
    await tx.objectStore('items').clear();
    await tx.objectStore('recipes').clear();
    await tx.objectStore('history').clear();
    await tx.objectStore('favorites').clear();
    await tx.objectStore('settings').clear();
    for (const item of bundle.items) await tx.objectStore('items').put(item);
    for (const recipe of bundle.recipes) await tx.objectStore('recipes').put(recipe);
    for (const h of bundle.history) await tx.objectStore('history').put(h);
    for (const f of bundle.favorites) await tx.objectStore('favorites').put(f);
    for (const [k, v] of Object.entries(bundle.settings)) await tx.objectStore('settings').put(v, k);
    await tx.objectStore('meta').put(true, 'seeded');
    await tx.done;
    return { items: bundle.items.length, recipes: bundle.recipes.length };
  },

  async resetSave(): Promise<void> {
    const db = await getDB();
    const tx = db.transaction(['items', 'recipes', 'history', 'favorites', 'settings', 'meta'], 'readwrite');
    await tx.objectStore('items').clear();
    await tx.objectStore('recipes').clear();
    await tx.objectStore('history').clear();
    await tx.objectStore('favorites').clear();
    await tx.objectStore('settings').clear();
    await tx.objectStore('meta').clear();
    await tx.done;
    await markInitialized();
  },

  isSaveFile,
};
